import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { getApiUrl } from '../../api'

interface ApplicationItem {
  application_id: number
  program_id: number
  program_name: string
  session_name: string | null
  participation_start_at: string | null
  participation_end_at: string | null
  applied_at: string
  application_status: string
}

export default function ProgramCheck() {
  const navigate = useNavigate()
  const location = useLocation()

  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [loading, setLoading] = useState(true)
  const [searching, setSearching] = useState(false)

  const [applications, setApplications] = useState<ApplicationItem[]>([])
  const [total, setTotal] = useState(0)

  const [guestName, setGuestName] = useState('')
  const [guestPhone, setGuestPhone] = useState('')
  const [searched, setSearched] = useState(false)

  useEffect(() => {
    checkLoginAndFetch()
  }, [])

  const checkLoginAndFetch = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('access_token')
      if (token) {
        const memberRes = await fetch(getApiUrl('/marubit/api/member/me'), {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (memberRes.ok) {
          setIsLoggedIn(true)
          await fetchMemberApplications(token)
          return
        }
      }

      // 비로그인: 신청 직후 전달된 이름/전화번호가 있으면 자동 조회
      const st = location.state as { guest_name?: string; guest_phone?: string } | null
      if (st?.guest_name && st?.guest_phone) {
        setGuestName(st.guest_name)
        setGuestPhone(st.guest_phone)
        await runGuestSearch(st.guest_name, st.guest_phone)
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchMemberApplications = async (token: string) => {
    const res = await fetch(getApiUrl('/marubit/api/program/applications/me?limit=100'), {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (res.ok) {
      const data = await res.json()
      setApplications(data.applications)
      setTotal(data.total)
    }
  }

  const runGuestSearch = async (name: string, phone: string) => {
    setSearching(true)
    try {
      const params = new URLSearchParams()
      params.append('guest_name', name)
      params.append('guest_phone', phone)
      params.append('limit', '100')

      const res = await fetch(getApiUrl(`/marubit/api/program/kiosk/guest/applications?${params.toString()}`))
      if (res.ok) {
        const data = await res.json()
        setApplications(data.applications)
        setTotal(data.total)
        setSearched(true)
      }
    } finally {
      setSearching(false)
    }
  }

  const fetchGuestApplications = () => {
    if (!guestName.trim() || !guestPhone.trim()) {
      alert('이름과 전화번호를 입력해주세요.')
      return
    }
    runGuestSearch(guestName.trim(), guestPhone.trim())
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    })
  }

  const formatDateTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })

  const statusColor = (status: string) => {
    if (status === '신청취소') return 'text-red-400 bg-red-50'
    if (status === '예약완료') return 'text-green-600 bg-green-50'
    if (status === '프로그램종료') return 'text-gray-400 bg-gray-100'
    return 'text-[#887267] bg-[#f5f2f0]'
  }

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  )

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 타이틀 */}
        <div className="mb-8">
          <h1 className="text-[26px] font-extrabold text-[#5a4035] mb-2">프로그램 신청 확인</h1>
          <p className="text-[14px] text-gray-400">신청한 프로그램 내역을 확인할 수 있습니다.</p>
        </div>

        {/* 비회원 검색 폼 */}
        {!isLoggedIn && (
          <div className="bg-white rounded-2xl p-7 mb-6 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
            <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-1">
              신청 내역 조회
            </h2>
            <p className="text-[12px] text-gray-400 ml-[13px] mb-4">
              신청 시 입력한 이름과 전화번호를 입력해주세요.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                placeholder="이름"
                value={guestName}
                onChange={e => setGuestName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchGuestApplications()}
                className="flex-1 px-4 py-3 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors"
              />
              <input
                type="tel"
                placeholder="전화번호"
                value={guestPhone}
                onChange={e => setGuestPhone(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchGuestApplications()}
                className="flex-1 px-4 py-3 text-[14px] border-[1.5px] border-gray-200 rounded-[10px] outline-none focus:border-[#887267] transition-colors"
              />
              <button
                onClick={fetchGuestApplications}
                disabled={searching}
                className="px-6 py-3 bg-[#887267] text-white text-[14px] font-bold rounded-[10px] hover:bg-[#6b5248] transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {searching ? '조회 중...' : '조회하기'}
              </button>
            </div>
            <div className="mt-3 text-center">
              <button
                onClick={() => navigate('/auth/login')}
                className="text-[13px] text-[#887267] underline"
              >
                회원이신가요? 로그인하기
              </button>
            </div>
          </div>
        )}

        {/* 신청 목록 */}
        {(isLoggedIn || searched) && (
          <div>
            <p className="text-[14px] text-gray-400 mb-4">총 {total}건</p>

            {applications.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center text-gray-300 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
                신청 내역이 없습니다.
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {applications.map((app) => (
                  <div
                    key={app.application_id ?? `${app.program_name}-${app.applied_at}`}
                    onClick={() => {
                    if (app.application_id) {
                        if (isLoggedIn) {
                        navigate(`/program/check/${app.application_id}`)
                        } else {
                        navigate(`/program/check/${app.application_id}`, {
                            state: { guest_name: guestName, guest_phone: guestPhone }
                        })
                        }
                    }
                    }}
                    className="bg-white rounded-2xl p-5 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)] flex items-center justify-between gap-4 cursor-pointer hover:shadow-[0_4px_20px_rgba(112,48,160,0.15)] transition-shadow"
                    >
                    <div className="flex-1 min-w-0">
                      <p className="text-[15px] font-bold text-[#222] mb-1 truncate">{app.program_name}</p>
                      {app.session_name && (
                        <p className="text-[13px] text-[#887267] font-medium mb-1">{app.session_name}</p>
                      )}
                      {(app.participation_start_at || app.participation_end_at) && (
                        <p className="text-[12px] text-gray-400 mb-1">
                          참여일 {formatDate(app.participation_start_at)} ~ {formatDate(app.participation_end_at)}
                        </p>
                      )}
                      <p className="text-[12px] text-gray-300">{formatDateTime(app.applied_at)} 신청</p>
                    </div>
                    <span className={`shrink-0 px-3 py-1 text-[12px] font-semibold rounded-full ${statusColor(app.application_status)}`}>
                      {app.application_status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}